import { util } from 'echarts/core';
import { type YAXisOption } from 'echarts/types/dist/shared';
import { type ThemeParser } from '../../../core/theme';
import type { Option } from '../../../core/option';
import { FONT_LINEHEIGHT_LIST, FONT_SIZE_LIST, getFontColor } from '../static/font';
import { type HorizontalAlign } from 'echarts/types/src/util/types';
import { isNumber } from 'zrender/lib/core/util';
import { normalizeToArray } from '../../../util/utils';
import { judgeIsDynamicBar } from '../series/dynamicBar';
import { getYAxisSplitLineStyle } from '../static/color';

type DvYAxisOption = Option['yAxis'] & {
  nameTextStyle?: {
    align: HorizontalAlign | number;
  };
};
export function preprocessPCYAxisOption(option: Parameters<ThemeParser>[0]) {
  option.yAxis = normalizeToArray(option.yAxis || {}) as DvYAxisOption[];
}

export function parseDynamicHistogramYAxis(option: Parameters<ThemeParser>[0]) {
  option.yAxis = normalizeToArray(option.yAxis || {});
  option.yAxis = option.yAxis.map(yAxis =>
    util.merge(
      {
        type: 'category',
        inverse: true,
        max: 18,
        axisTick: {
          show: false
        },
        axisLine: {
          show: false,
          lineStyle: {
            type: 'dashed',
            dashOffset: 2,
            color: '#333333'
          }
        },
        axisLabel: {
          show: true,
          align: 'left',
          margin: 114,
          formatter(value, index) {
            return `{rank${index > 2 ? '' : index + 1}|${index + 1}}  {normal|${value}}`;
          },
          rich: {
            normal: {
              fontWeight: 400,
              fontSize: 14,
              color: '#272841'
            },
            highlight: {
              fontWeight: 600,
              fontSize: 14,
              color: '#272841'
            },
            rank: {
              color: 'rgba(0, 0, 0, 0.32)',
              fontSize: 14,
              fontWeight: 600
            },
            rank1: {
              color: 'rgb(250, 59, 50)',
              fontSize: 14,
              fontWeight: 600
            },
            rank2: {
              color: 'rgb(255, 167, 66)',
              fontSize: 14,
              fontWeight: 600
            },
            rank3: {
              color: 'rgb(255, 214, 66)',
              fontSize: 14,
              fontWeight: 600
            },
            flag: {
              fontSize: 25,
              padding: 5
            }
          }
        },
        animationDuration: 300,
        animationDurationUpdate: 300
      },
      yAxis || {},
      true
    )
  );
}
export function parsePCYAxisStyle(
  option: Parameters<ThemeParser>[0],
  chart: Parameters<ThemeParser>[1]
) {
  const themeMode = chart.getModel().getThemeName().includes('light') ? 'light' : 'dark';
  (option.yAxis as DvYAxisOption[])?.forEach((yAxisOption: DvYAxisOption) => {
    const defaultYAxis: YAXisOption = {
      axisTick: {
        show: false
      },
      axisLabel: {
        show: true,
        margin: 8,
        fontSize: FONT_SIZE_LIST.axisLabel,
        color: getFontColor('axisLabel', themeMode)
      },
      splitLine: {
        dvZ: 0,
        lineStyle: getYAxisSplitLineStyle(themeMode)
      }
    };
    if (yAxisOption.name) {
      // PC端范式规范默认配置
      // 距离x轴线的距离
      const fontHeight = (yAxisOption?.axisLabel?.fontSize || FONT_SIZE_LIST.axisLabel) as number;
      defaultYAxis.nameGap = fontHeight / 2 + 8;
      defaultYAxis.nameLocation = 'end';
      defaultYAxis.nameTextStyle = {
        fontSize: FONT_SIZE_LIST.axisName,
        color: getFontColor('axisName', themeMode),
        lineHeight: FONT_LINEHEIGHT_LIST.axisName,
        align: 'center',
        padding: [0, 0, 0, 16]
      };
    }
    util.merge(yAxisOption, defaultYAxis);
  });
}

function percentToNumber(percent: string | number, base: number): number {
  if (typeof percent === 'number') {
    return percent;
  }
  return (Number(percent.split('%')[0]) / 100) * base;
}

// pc范式y轴分割线
export function parsePCYAxisLayout(
  option: Parameters<ThemeParser>[0],
  chart: Parameters<ThemeParser>[1]
) {
  // @ts-ignore
  // eslint-disable-next-line no-param-reassign
  option.yAxis = normalizeToArray(option.yAxis);
  const dom = chart.getDom();
  const domHeight = dom.getBoundingClientRect().height;
  option.grid?.forEach((g, i) => {
    let gridHeight = g.height;
    if (!isNumber(gridHeight)) {
      const gridTop = percentToNumber(g.top, domHeight);
      const gridBottom = percentToNumber(g.bottom, domHeight);
      gridHeight = domHeight - gridBottom - gridTop;
    }
    const splitNumber = Math.min(Math.max(1, Math.floor(gridHeight / 32)), 5);
    const targetYAxis = option.yAxis.find(el => {
      if (el.gridIndex === undefined) {
        return i === 0;
      }
      return el.gridIndex === i;
    });
    targetYAxis.dvSplitLineNumber = splitNumber + 1;
  });
}

export function yAxisThemeParse(...args: Parameters<ThemeParser>): ReturnType<ThemeParser> {
  const option = args[0];
  const chart = args[1];
  const series = normalizeToArray(option.series);
  preprocessPCYAxisOption(option);
  if (judgeIsDynamicBar(series)) {
    parseDynamicHistogramYAxis(option);
  } else {
    parsePCYAxisLayout(option, chart);
    parsePCYAxisStyle(option, chart);
  }
}
